import { Plus, Trash2, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import type { MealSlot } from "@/hooks/useFoodMenu";

interface Props {
  slot: MealSlot;
  removable: boolean;
  onChange: (slot: MealSlot) => void;
  onRemove: () => void;
}

export const MealSlotRow = ({ slot, removable, onChange, onRemove }: Props) => {
  const patch = (p: Partial<MealSlot>) => onChange({ ...slot, ...p });

  const addItem = () => patch({ items: [...slot.items, ""] });

  const updateItem = (idx: number, value: string) => {
    const next = [...slot.items];
    next[idx] = value;
    patch({ items: next });
  };

  const removeItem = (idx: number) =>
    patch({ items: slot.items.filter((_, i) => i !== idx) });

  return (
    <section className="bg-card rounded-card border border-border p-4 space-y-3">
      <div className="flex items-start gap-3">
        <div className="flex-1 grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Label</label>
            <Input
              value={slot.label}
              onChange={(e) => patch({ label: e.target.value })}
              placeholder="e.g. Breakfast"
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">
              Time (HH:MM)
            </label>
            <Input
              value={slot.timeOfDay}
              onChange={(e) => patch({ timeOfDay: e.target.value })}
              placeholder="07:30"
              maxLength={5}
            />
          </div>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="shrink-0 text-destructive hover:text-destructive mt-6"
          disabled={!removable}
          onClick={onRemove}
          title={!removable ? "Minimum 1 slot required" : "Remove slot"}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>

      <div className="space-y-1.5">
        <div className="flex items-center justify-between">
          <label className="text-xs text-muted-foreground">Food items</label>
          <span className="text-[11px] text-muted-foreground">
            Use "or" or "/" for choice options (e.g. Dosa or Porotta)
          </span>
        </div>
        {slot.items.map((item, ii) => (
          <div key={ii} className="flex items-center gap-2">
            <Input
              value={item}
              onChange={(e) => updateItem(ii, e.target.value)}
              placeholder={`e.g. Milk or Egg or Dosa / Chicken Curry or Porotta`}
              className="flex-1"
            />
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={() => removeItem(ii)}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        ))}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="text-primary h-7 px-2"
          onClick={addItem}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> Add item
        </Button>
      </div>

      <div className="border-t border-border pt-3 space-y-2">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium">Available to others</p>
            <p className="text-xs text-muted-foreground">
              Allow family members
            </p>
          </div>
          <Switch
            checked={slot.allowOthers}
            onCheckedChange={(v) => patch({ allowOthers: v })}
          />
        </div>
        {slot.allowOthers && (
          <div className="space-y-1 pt-1">
            <label className="text-xs text-muted-foreground">
              Serves (people)
            </label>
            <Input
              type="number"
              min={1}
              value={slot.servingCapacity}
              onChange={(e) =>
                patch({ servingCapacity: Math.max(1, Number(e.target.value)) })
              }
              className="w-32"
            />
          </div>
        )}
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium">Served on this day</p>
          <Switch
            checked={slot.isAvailable}
            onCheckedChange={(v) => patch({ isAvailable: v })}
          />
        </div>
      </div>
    </section>
  );
};
